import { useDefaultParameterVisit } from "./useDefaultParameterVisit";
import { DefaultParameterStatus } from "./DefaultParameterStatus";
import { useCallback, useEffect, useMemo } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { AddFixedMixButton } from "./AddFixedMixButton";
import { RevertDefaultsButton } from "./RevertDefaultsButton";
import { useSearchParams } from "react-router";
import { useTenant } from "hooks/settings";
import { useTenantTranslation } from "hooks/formatters";
import { useNotifyStatus } from "contexts/status";
import { isFetchBaseQueryError } from "helpers";
import {
  FixedMixDefaultsRead,
  FixedMixCreate,
  FixedMixImpurities,
  MaterialRead,
  SteelGrade,
  ParameterConflict,
  useGetFixedMixDefaultsQuery,
  useUpdateFixedMixDefaultsMutation,
  useGetMaterialMetadataSetQuery,
  useGetSteelGradeSetQuery,
  useGetFormattedBasketConstraintsQuery,
  useGetChefGroupingQuery,
} from "store/api/generatedApi";
import { useDefaultParametersContext } from "./context";
import { editableFixedMixes, fixedMixesMatch } from "./fixedMixEdits";
import { useFixedMixDraft } from "./useFixedMixDraft";
import { useFixedMixPreview } from "./useFixedMixPreview";
import {
  splitFixedMixGroups,
  fixedMixGroupsMatch,
  restoreFixedMixGroup,
  revertFixedMixGrades,
  fixedMixDraftsMatch,
} from "./fixedMixRows";
import { FixedMixDefaultsTable } from "./FixedMixDefaultsTable";
import { MaterialPropertiesLegend } from "./MaterialPropertiesLegend";
import { ConflictWarningBanner } from "./ConflictWarningBanner";
import { useLimitLabelBySignature } from "./useLimitLabelBySignature";

const EMPTY_CONFLICTS: ParameterConflict[] = [];
const EMPTY_CHEMISTRY: FixedMixImpurities[] = [];

export const FixedMixDefaultParameters = ({
  isActive,
  setConfirmOpen,
}: {
  isActive: boolean;
  setConfirmOpen: (open: boolean) => void;
}) => {
  const tenantName = useTenant();
  const { t } = useTenantTranslation();
  const defaults = useGetFixedMixDefaultsQuery(
    { tenantName },
    { refetchOnMountOrArgChange: true }
  );
  const materials = useGetMaterialMetadataSetQuery({
    tenantName,
    materialMetadataSetId: "default",
  });
  const steelGrades = useGetSteelGradeSetQuery({
    tenantName,
    steelGradeSetId:
      defaults.currentData?.effective.steel_grade_set_id ?? "default",
  });
  const baskets = useGetFormattedBasketConstraintsQuery({ tenantName });
  const { refetch } = defaults;
  useEffect(() => {
    if (isActive) void refetch();
  }, [isActive, refetch]);
  if (
    (defaults.isError && !defaults.currentData) ||
    (materials.isError && !materials.currentData) ||
    (baskets.isError && !baskets.currentData) ||
    (steelGrades.isError && !steelGrades.currentData)
  )
    return <Alert severity="error">{t("errorLoadingData")}</Alert>;
  if (
    !defaults.currentData ||
    !materials.currentData ||
    !baskets.currentData ||
    !steelGrades.currentData
  )
    return <CircularProgress />;
  return (
    <FixedMixDefaultsEditor
      key={tenantName}
      isActive={isActive}
      setConfirmOpen={setConfirmOpen}
      defaults={defaults.currentData}
      materials={materials.currentData.materials}
      steelGrades={steelGrades.currentData.steel_grades}
      basketNumbers={baskets.currentData
        .filter((basket) => basket.basket_id !== null)
        .map((basket) => basket.basket_number)}
      isFetching={
        defaults.isFetching ||
        materials.isFetching ||
        steelGrades.isFetching ||
        baskets.isFetching
      }
      refresh={defaults.refetch}
    />
  );
};

const FixedMixDefaultsEditor = ({
  isActive,
  setConfirmOpen,
  defaults,
  materials,
  steelGrades,
  basketNumbers,
  isFetching,
  refresh,
}: {
  isActive: boolean;
  setConfirmOpen: (open: boolean) => void;
  defaults: FixedMixDefaultsRead;
  materials: MaterialRead[];
  steelGrades: SteelGrade[];
  basketNumbers: number[];
  isFetching: boolean;
  refresh: () => Promise<unknown>;
}) => {
  const tenantName = useTenant();
  useDefaultParameterVisit({
    tenantName,
    page: "predefined_mixes",
    isActive,
  });
  const { t } = useTenantTranslation();
  const notify = useNotifyStatus();
  const { setTabActions } = useDefaultParametersContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    source,
    setDraft,
    discard,
    save,
    isStale,
    sourceMixes,
    groups,
    request,
    previewRequest,
  } = useFixedMixDraft(defaults);
  const scrapchefMixes = useMemo(
    () => editableFixedMixes(defaults.scrapchef.mixes),
    [defaults]
  );
  const scrapchefGroups = useMemo(
    () =>
      [...new Set(defaults.scrapchef.mixes.map((recipe) => recipe.mix.id))].map(
        (mixId) =>
          defaults.scrapchef.mixes
            .filter((recipe) => recipe.mix.id === mixId)
            .map((recipe) => recipe.steel_grade_name)
      ),
    [defaults.scrapchef.mixes]
  );
  const scrapchefPreview = defaults.scrapchef_preview ?? null;
  const savedPreview = source.effective_preview ?? null;
  const checked = useFixedMixPreview(previewRequest);
  const preview = previewRequest === null ? savedPreview : checked.preview;
  const { isChecking, retry } = checked;
  const hasError =
    checked.hasError || (previewRequest === null && savedPreview === null);
  const displayedMixes = preview?.mixes ?? request.mixes;
  const conflicts = preview?.feasibility.conflicts ?? EMPTY_CONFLICTS;
  const scrapchefBaseline =
    scrapchefPreview === null
      ? null
      : {
          mixes: scrapchefPreview.mixes,
          mixGroups: scrapchefGroups,
        };
  const matchesScrapchefDefaults =
    !defaults.effective_preview || scrapchefBaseline === null
      ? null
      : fixedMixDraftsMatch(
          {
            mixes: defaults.effective_preview.mixes,
            mixGroups: defaults.mix_groups,
          },
          scrapchefBaseline
        );
  const canRevert =
    scrapchefBaseline !== null &&
    !isChecking &&
    (preview === null ||
      !fixedMixDraftsMatch(
        { mixes: preview.mixes, mixGroups: request.mix_groups },
        scrapchefBaseline
      ));

  const hasChanges =
    !fixedMixesMatch(displayedMixes, sourceMixes) ||
    !fixedMixGroupsMatch(request.mix_groups, source.mix_groups);
  const [update, { isLoading: isSaving }] = useUpdateFixedMixDefaultsMutation();
  const grouping = useGetChefGroupingQuery({
    tenantName,
    chefGroupingId: "default",
  });
  const chefGroupNameById = useMemo(
    () =>
      Object.fromEntries(
        (grouping.currentData?.chef_groups ?? []).flatMap((group) =>
          group.id == null ? [] : [[group.id, group.name] as const]
        )
      ),
    [grouping.currentData]
  );
  const limitLabelBySignature = useLimitLabelBySignature();
  const onSave = useCallback(async () => {
    if (isStale || isSaving || !hasChanges || hasError) return;
    try {
      await save(async () => {
        await update({ tenantName, fixedMixDefaultsRequest: request }).unwrap();
        await refresh();
      });
      notify({ text: t("defaultsSaved"), type: "success" });
    } catch (error) {
      const stale = isFetchBaseQueryError(error) && error.status === 409;
      notify({
        text: t(stale ? "defaultsOutOfDate" : "errorSavingChanges"),
        type: "error",
      });
      if (stale) await refresh();
    } finally {
      setConfirmOpen(false);
    }
  }, [
    hasError,
    isStale,
    isSaving,
    hasChanges,
    save,
    update,
    tenantName,
    request,
    refresh,
    notify,
    t,
    setConfirmOpen,
  ]);
  useEffect(() => {
    if (!isActive) return;
    setTabActions({
      hasChanges,
      isSaving,
      isChecking,
      conflicts,
      onSave,
      onDiscard: discard,
      saveAvailability: isStale || hasError ? "blocked" : "ready",
    });
    return () => {
      setTabActions(null);
    };
  }, [
    isActive,
    setTabActions,
    hasChanges,
    isSaving,
    isChecking,
    conflicts,
    onSave,
    discard,
    isStale,
    hasError,
  ]);
  const selectedGrades = useMemo(
    () => searchParams.getAll("grade"),
    [searchParams]
  );
  const onSplit = useCallback(
    (gradeNames: string[]) => {
      setDraft({
        mixes: displayedMixes,
        mixGroups: splitFixedMixGroups({
          mixGroups: request.mix_groups,
          gradeNames,
        }),
      });
    },
    [setDraft, displayedMixes, request.mix_groups]
  );
  const onChange = useCallback(
    (mixes: FixedMixCreate[]) => {
      setDraft({ mixes, mixGroups: request.mix_groups });
    },
    [setDraft, request.mix_groups]
  );
  const onRevert = useCallback(
    (gradeNames: string[]) => {
      if (!scrapchefPreview) return;
      setDraft(
        revertFixedMixGrades({
          mixes: displayedMixes,
          mixGroups: request.mix_groups,
          defaults: scrapchefPreview.mixes,
          gradeNames,
        })
      );
    },
    [setDraft, displayedMixes, request.mix_groups, scrapchefPreview]
  );
  const onDiscard = useCallback(
    (gradeNames: string[]) => {
      setDraft(
        restoreFixedMixGroup({
          mixes: displayedMixes,
          mixGroups: request.mix_groups,
          sourceMixes: savedPreview?.mixes ?? sourceMixes,
          sourceGroups: source.mix_groups,
          gradeNames,
        })
      );
    },
    [
      setDraft,
      displayedMixes,
      request.mix_groups,
      savedPreview,
      sourceMixes,
      source.mix_groups,
    ]
  );
  return (
    <Stack gap={2} sx={{ flex: 1, minHeight: 0 }}>
      <Typography variant="h2">{t("fixedMixOverrides")}</Typography>
      <Stack direction="row" gap={2} alignItems="center">
        <Typography color="text.secondary">
          {t("fixedMixDefaultsDescription")}
        </Typography>
        <DefaultParameterStatus
          matchesScrapchefDefaults={matchesScrapchefDefaults}
        />
      </Stack>
      <Stack direction="row" gap={2} alignItems="center">
        <MaterialPropertiesLegend />
        <AddFixedMixButton
          steelGrades={steelGrades}
          mixes={displayedMixes}
          mixGroups={request.mix_groups}
          disabled={isSaving || isStale || isChecking || isFetching}
          onAdd={(gradeNames, recipe) => {
            setDraft({
              mixes: [
                ...displayedMixes,
                ...gradeNames.map((steel_grade_name) => ({
                  steel_grade_name,
                  mix: recipe,
                })),
              ],
              mixGroups: [...request.mix_groups, gradeNames],
            });
            setSearchParams((previous) => {
              const updated = new URLSearchParams(previous);
              updated.delete("grade");
              return updated;
            });
          }}
        />
        <RevertDefaultsButton
          disabled={!canRevert || isSaving || isStale}
          warning={t("revertPredefinedMixesWarning")}
          onRevert={() => {
            setDraft({
              mixes: scrapchefMixes,
              mixGroups: scrapchefGroups,
            });
          }}
        />
      </Stack>
      {selectedGrades.length > 0 ? (
        <Button
          onClick={() => {
            setSearchParams((previous) => {
              const updated = new URLSearchParams(previous);
              updated.delete("grade");
              return updated;
            });
          }}
        >
          {t("showAllFixedMixes")}
        </Button>
      ) : null}
      {isStale ? (
        <Alert
          severity="warning"
          action={
            <Button color="inherit" onClick={discard}>
              {t("discardChanges")}
            </Button>
          }
        >
          {t("defaultsOutOfDate")}
        </Alert>
      ) : null}
      {hasError || scrapchefPreview === null ? (
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              onClick={() => {
                void refresh().then(() => {
                  retry();
                });
              }}
            >
              {t("refresh")}
            </Button>
          }
        >
          {t("fixedMixPreviewFailed")}
        </Alert>
      ) : null}
      <ConflictWarningBanner
        hasError={false}
        conflicts={conflicts}
        isChecking={isChecking}
        chefGroupNameById={chefGroupNameById}
        limitLabelBySignature={limitLabelBySignature}
      />
      <FixedMixDefaultsTable
        mixes={displayedMixes}
        groups={groups}
        chemistry={preview?.chemistry ?? EMPTY_CHEMISTRY}
        chefGroupNameById={chefGroupNameById}
        limitLabelBySignature={limitLabelBySignature}
        onSplit={onSplit}
        savedMixes={source.effective.mixes}
        savedMixGroups={source.mix_groups}
        selectedGrades={selectedGrades}
        steelGrades={steelGrades}
        basketNumbers={basketNumbers}
        loading={isChecking || isFetching}
        source={savedPreview?.mixes}
        scrapchef={scrapchefPreview?.mixes}
        materials={materials}
        conflicts={conflicts}
        disabled={isSaving || isStale}
        onChange={onChange}
        onRevert={onRevert}
        onDiscard={onDiscard}
      />
    </Stack>
  );
};
